import { useState } from 'react';
import {
  radiosConProgramacion,
  programas,
  eventosAutomaticos,
  tvAppData,
  statsProgramacionAutomatica,
  reglasProgramacionAutomatica,
  categoriaMeta,
  vistaOyenteMeta,
  resumenGlobal,
  type VistaOyente,
  type RadioConProgramacion,
  type ProgramaCompleto,
} from '@/mocks/programacionAutomatica';

export default function ProgramacionAutomaticaSection() {
  const [tabActiva, setTabActiva] = useState<string>('ahora');
  const [radioSeleccionada, setRadioSeleccionada] = useState<RadioConProgramacion>(radiosConProgramacion[0]);

  const radioActual = radiosConProgramacion.find((r) => r.id === radioSeleccionada.id) ?? radiosConProgramacion[0];

  const tabs = [
    { id: 'ahora', label: 'Ahora', icono: 'ri-play-circle-line' },
    { id: 'siguiente', label: 'Siguiente', icono: 'ri-skip-forward-line' },
    { id: 'mas-tarde', label: 'Más Tarde', icono: 'ri-time-line' },
    { id: 'semana', label: 'Semana', icono: 'ri-calendar-line' },
    { id: 'tv-app', label: 'TV App', icono: 'ri-tv-2-line' },
    { id: 'reglas', label: 'Reglas', icono: 'ri-scales-line' },
  ];

  const stats = [
    { label: 'Radios', value: statsProgramacionAutomatica.totalRadiosConProgramacion, icono: 'ri-radio-line' },
    { label: 'Programas Activos', value: statsProgramacionAutomatica.totalProgramasActivos, icono: 'ri-calendar-check-line' },
    { label: 'Eventos Hoy', value: statsProgramacionAutomatica.eventosAutomaticosHoy, icono: 'ri-flashlight-line' },
    { label: 'Precisión', value: statsProgramacionAutomatica.precisionHoraria, icono: 'ri-timer-flash-line' },
  ];

  return (
    <section className="py-14 md:py-20 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-rose-50 border border-rose-200/60 mb-5">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-rose-500"></span>
            </span>
            <span className="text-xs font-semibold text-rose-700 tracking-wide uppercase">Programación Automática</span>
          </div>
          <h2 className="text-2xl md:text-4xl font-bold text-foreground-950 mb-3">
            Tu programación se activa{' '}
            <span className="bg-gradient-to-r from-rose-500 to-amber-500 bg-clip-text text-transparent">sola</span>
          </h2>
          <p className="text-foreground-600 max-w-2xl mx-auto text-sm md:text-base">
            Cuando llega la hora, todo se activa automáticamente: el programa, el locutor, las notificaciones a oyentes, 
            la cabina y hasta la TV App. Sin que tengas que hacer nada.
          </p>
        </div>

        {/* Stats bar */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-5 mb-10">
          {stats.map((s) => (
            <div key={s.label} className="bg-background-50 border border-background-200/70 rounded-xl p-4 text-center hover:border-background-300/60 transition-colors">
              <i className={`${s.icono} text-foreground-400 text-lg mb-2 block`}></i>
              <div className="text-xl md:text-2xl font-bold text-foreground-950">{s.value}</div>
              <div className="text-xs text-foreground-500 mt-1">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Radio selector */}
        <div className="flex flex-wrap items-center gap-2 mb-8 justify-center">
          <span className="text-xs font-medium text-foreground-500 mr-2">Radio:</span>
          {radiosConProgramacion.map((radio) => (
            <button
              key={radio.id}
              onClick={() => setRadioSeleccionada(radio)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                radioSeleccionada.id === radio.id
                  ? 'bg-rose-500 text-white'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200/80'
              }`}
            >
              {radio.frecuencia} FM
            </button>
          ))}
        </div>

        {/* Radio info card */}
        <div className="bg-background-50 border border-background-200/70 rounded-2xl p-5 md:p-6 mb-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0">
              <img src={radioActual.logo} alt={radioActual.nombre} className="w-full h-full object-cover" loading="lazy" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-lg font-bold text-foreground-950">{radioActual.nombre}</h3>
                <span className="px-2 py-0.5 rounded-md bg-rose-100 text-rose-700 text-xs font-semibold">{radioActual.frecuencia} FM</span>
                {radioActual.enVivo && (
                  <span className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-emerald-100 text-emerald-700 text-xs font-semibold">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>EN VIVO
                  </span>
                )}
              </div>
              <p className="text-sm text-foreground-500 mt-1">{radioActual.ciudad}, {radioActual.pais} · {radioActual.categoria} · {radioActual.oyentesActuales.toLocaleString()} oyentes</p>
            </div>
            {radioActual.programaActual && (
              <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200/60 rounded-lg flex-shrink-0">
                <i className="ri-broadcast-line text-amber-600"></i>
                <span className="text-xs font-medium text-amber-700 whitespace-nowrap">Al aire: {radioActual.programaActual.nombre}</span>
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex overflow-x-auto gap-1 mb-8 p-1 bg-background-100 rounded-full">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setTabActiva(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium transition-all whitespace-nowrap cursor-pointer ${
                tabActiva === tab.id
                  ? 'bg-white text-foreground-950 shadow-sm'
                  : 'text-foreground-500 hover:text-foreground-700'
              }`}
            >
              <i className={`${tab.icono} text-sm`}></i>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className="min-h-[300px]">
          {tabActiva === 'ahora' && <TabAhora radio={radioActual} />}
          {tabActiva === 'siguiente' && <TabSiguiente radio={radioActual} />}
          {tabActiva === 'mas-tarde' && <TabMasTarde radio={radioActual} />}
          {tabActiva === 'semana' && <TabSemana radio={radioActual} />}
          {tabActiva === 'tv-app' && <TabTVApp radio={radioActual} />}
          {tabActiva === 'reglas' && <TabReglas />}
        </div>

        {/* CTA */}
        <div className="mt-12 text-center">
          <a
            href="/programacion-automatica"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-rose-500 to-amber-500 text-white font-semibold text-sm hover:from-rose-600 hover:to-amber-600 transition-all shadow-sm cursor-pointer whitespace-nowrap"
          >
            <i className="ri-calendar-check-line"></i>
            Ver sistema completo de programación automática
            <i className="ri-arrow-right-line"></i>
          </a>
          <p className="text-xs text-foreground-400 mt-3">5 reglas. 15 programas. 7 radios. 0 intervención manual.</p>
        </div>
      </div>
    </section>
  );
}

/* ─── TAB: AHORA ─── */
function TabAhora({ radio }: { radio: RadioConProgramacion }) {
  if (!radio.programaActual) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
          <i className="ri-play-circle-line text-3xl text-foreground-300"></i>
        </div>
        <p className="text-foreground-500 text-sm">No hay ningún programa al aire en este momento.</p>
        {radio.programaSiguiente && (
          <p className="text-foreground-400 text-xs mt-2">Próximo: {radio.programaSiguiente.nombre} a las {radio.programaSiguiente.horarios[0].inicio}</p>
        )}
      </div>
    );
  }

  const prog = radio.programaActual;
  const cat = categoriaMeta[prog.categoria];

  return (
    <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200/50 rounded-2xl p-6 md:p-8">
      <div className="flex items-center gap-3 mb-5">
        <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500 text-white text-xs font-semibold">
          <span className="w-2 h-2 rounded-full bg-white animate-pulse"></span>
          EN VIVO AHORA
        </div>
        <span className={`px-2.5 py-0.5 rounded-md ${cat.bgColor} ${cat.textColor} text-xs font-medium`}>
          <i className={`${cat.icono} mr-1`}></i>{prog.categoria}
        </span>
      </div>

      <div className="flex flex-col md:flex-row gap-6">
        <div className="w-full md:w-48 h-32 rounded-xl overflow-hidden flex-shrink-0">
          <img src={prog.portada} alt={prog.nombre} className="w-full h-full object-cover" loading="lazy" />
        </div>
        <div className="flex-1">
          <h3 className="text-xl md:text-2xl font-bold text-foreground-950 mb-1">{prog.nombre}</h3>
          <p className="text-sm text-foreground-600 mb-3">{prog.descripcion.slice(0, 200)}...</p>
          <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-500">
            <span className="flex items-center gap-1"><i className="ri-user-voice-line"></i>{prog.locutores[0].nombre}</span>
            <span className="flex items-center gap-1"><i className="ri-time-line"></i>{prog.horarios[0].inicio} - {prog.horarios[0].fin}</span>
            <span className="flex items-center gap-1"><i className="ri-timer-line"></i>{prog.duracion}</span>
          </div>
          <div className="mt-4 flex flex-wrap gap-1.5">
            {prog.tags.map((tag) => (
              <span key={tag} className="px-2 py-0.5 bg-white/70 rounded-md text-xs text-foreground-500">{tag}</span>
            ))}
          </div>
        </div>
      </div>

      {/* Activación automática */}
      <div className="mt-6 p-4 bg-white/60 rounded-xl border border-emerald-100/60">
        <div className="flex items-center gap-2 mb-3">
          <i className="ri-flashlight-line text-amber-500"></i>
          <span className="text-xs font-semibold text-foreground-700">Activado automáticamente:</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {['Programa Activo', 'Locutor Al Aire', 'Notificaciones Enviadas', 'Cabina Actualizada'].map((item, i) => (
            <div key={i} className="flex items-center gap-2 text-xs text-foreground-600">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
              {item}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── TAB: SIGUIENTE ─── */
function TabSiguiente({ radio }: { radio: RadioConProgramacion }) {
  if (!radio.programaSiguiente) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
          <i className="ri-skip-forward-line text-3xl text-foreground-300"></i>
        </div>
        <p className="text-foreground-500 text-sm">No hay más programas programados para hoy.</p>
      </div>
    );
  }

  const prog = radio.programaSiguiente;
  const cat = categoriaMeta[prog.categoria];

  return (
    <div className="bg-gradient-to-br from-sky-50 to-cyan-50 border border-sky-200/50 rounded-2xl p-6 md:p-8">
      <div className="flex items-center gap-3 mb-5">
        <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-sky-500 text-white text-xs font-semibold">
          <i className="ri-skip-forward-line"></i>
          A CONTINUACIÓN
        </div>
        <span className={`px-2.5 py-0.5 rounded-md ${cat.bgColor} ${cat.textColor} text-xs font-medium`}>
          <i className={`${cat.icono} mr-1`}></i>{prog.categoria}
        </span>
      </div>
      <div className="flex flex-col md:flex-row gap-6">
        <div className="w-full md:w-48 h-32 rounded-xl overflow-hidden flex-shrink-0">
          <img src={prog.portada} alt={prog.nombre} className="w-full h-full object-cover" loading="lazy" />
        </div>
        <div className="flex-1">
          <h3 className="text-xl font-bold text-foreground-950 mb-1">{prog.nombre}</h3>
          <p className="text-sm text-foreground-600 mb-3">{prog.descripcion.slice(0, 180)}...</p>
          <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-500">
            <span className="flex items-center gap-1"><i className="ri-user-voice-line"></i>{prog.locutores[0].nombre}</span>
            <span className="flex items-center gap-1 font-semibold text-foreground-700">
              <i className="ri-time-line"></i>Empieza a las {prog.horarios[0].inicio}
            </span>
            <span className="flex items-center gap-1"><i className="ri-timer-line"></i>{prog.duracion}</span>
          </div>
        </div>
      </div>
      <div className="mt-6 p-4 bg-white/60 rounded-xl border border-sky-100/60">
        <div className="flex items-center gap-2">
          <i className="ri-notification-3-line text-sky-500"></i>
          <span className="text-xs text-foreground-600">
            A las {prog.horarios[0].inicio} se activará automáticamente: programa, locutor, notificaciones, cabina y TV App.
          </span>
        </div>
      </div>
    </div>
  );
}

/* ─── TAB: MÁS TARDE ─── */
function TabMasTarde({ radio }: { radio: RadioConProgramacion }) {
  const programasMasTarde = radio.programasHoy.filter(
    (p) => p !== radio.programaActual && p !== radio.programaSiguiente,
  );

  if (radio.programaSiguiente && !programasMasTarde.includes(radio.programaSiguiente)) {
    programasMasTarde.unshift(radio.programaSiguiente);
  }

  const todos = [...programasMasTarde];
  if (radio.programaActual && todos.includes(radio.programaActual)) {
    // no mostrar el actual en más tarde
  }

  const filtrados = todos.filter((p) => p !== radio.programaActual);

  if (filtrados.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center">
          <i className="ri-time-line text-3xl text-foreground-300"></i>
        </div>
        <p className="text-foreground-500 text-sm">No hay más programas para el resto del día.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {filtrados.map((prog, i) => {
        const cat = categoriaMeta[prog.categoria];
        return (
          <div key={prog.id} className="bg-background-50 border border-background-200/70 rounded-xl p-4 md:p-5 flex flex-col sm:flex-row gap-4 hover:border-background-300/60 transition-colors">
            <div className="w-full sm:w-32 h-20 rounded-lg overflow-hidden flex-shrink-0">
              <img src={prog.portada} alt={prog.nombre} className="w-full h-full object-cover" loading="lazy" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <h4 className="text-sm font-bold text-foreground-950">{prog.nombre}</h4>
                <span className={`px-1.5 py-0.5 rounded-md ${cat.bgColor} ${cat.textColor} text-[10px] font-medium`}>
                  {prog.categoria}
                </span>
              </div>
              <p className="text-xs text-foreground-500 line-clamp-2 mb-2">{prog.descripcion.slice(0, 120)}</p>
              <div className="flex flex-wrap items-center gap-3 text-[11px] text-foreground-400">
                <span className="flex items-center gap-1"><i className="ri-user-voice-line"></i>{prog.locutores[0].nombre}</span>
                <span className="flex items-center gap-1"><i className="ri-time-line"></i>{prog.horarios[0].inicio} - {prog.horarios[0].fin}</span>
                <span className="flex items-center gap-1"><i className="ri-calendar-line"></i>{prog.horarios[0].dia}</span>
              </div>
            </div>
            <div className="flex-shrink-0 flex items-center">
              <span className="text-xs font-semibold text-amber-600 bg-amber-50 px-2 py-1 rounded-md whitespace-nowrap">
                {prog.horarios[0].inicio}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ─── TAB: SEMANA ─── */
function TabSemana({ radio }: { radio: RadioConProgramacion }) {
  const dias: ('Lunes' | 'Martes' | 'Miércoles' | 'Jueves' | 'Viernes' | 'Sábado' | 'Domingo')[] = [
    'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado', 'Domingo',
  ];

  return (
    <div className="space-y-3">
      {dias.map((dia) => {
        const progsDelDia = radio.programasSemana.filter((p) =>
          p.horarios.some((h) => h.dia === dia),
        ).sort((a, b) => {
          const ha = a.horarios.find((h) => h.dia === dia)?.inicio ?? '';
          const hb = b.horarios.find((h) => h.dia === dia)?.inicio ?? '';
          return ha.localeCompare(hb);
        });

        if (progsDelDia.length === 0) return null;

        return (
          <div key={dia} className="bg-background-50 border border-background-200/70 rounded-xl p-4">
            <h4 className="text-xs font-semibold text-foreground-700 mb-3 uppercase tracking-wide">{dia}</h4>
            <div className="space-y-2">
              {progsDelDia.map((prog) => {
                const cat = categoriaMeta[prog.categoria];
                const horario = prog.horarios.find((h) => h.dia === dia);
                return (
                  <div key={`${prog.id}-${dia}`} className="flex items-center gap-3 p-2 rounded-lg hover:bg-background-100 transition-colors">
                    <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0">
                      <img src={prog.portada} alt={prog.nombre} className="w-full h-full object-cover" loading="lazy" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-foreground-900 truncate">{prog.nombre}</span>
                        <span className={`px-1 py-0.5 rounded ${cat.bgColor} ${cat.textColor} text-[9px] font-medium whitespace-nowrap`}>
                          {prog.categoria}
                        </span>
                      </div>
                      <span className="text-[11px] text-foreground-400">{prog.locutores[0].nombre}</span>
                    </div>
                    <span className="text-xs font-mono text-foreground-500 whitespace-nowrap">
                      {horario?.inicio} - {horario?.fin}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ─── TAB: TV APP ─── */
function TabTVApp({ radio }: { radio: RadioConProgramacion }) {
  const tv = tvAppData.find((t) => t.radioFrecuencia === radio.frecuencia);

  return (
    <div className="bg-gradient-to-br from-foreground-900 to-foreground-800 rounded-2xl p-6 md:p-8 text-white overflow-hidden relative">
      {/* TV frame */}
      <div className="absolute top-3 right-4 flex items-center gap-1.5">
        <span className="w-2 h-2 rounded-full bg-red-400"></span>
        <span className="text-[10px] text-white/60">REC</span>
      </div>

      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg overflow-hidden bg-white/10">
          <img src={radio.logo} alt={radio.nombre} className="w-full h-full object-cover" loading="lazy" />
        </div>
        <div>
          <p className="text-xs text-white/60">TV App · {radio.frecuencia} FM</p>
          <p className="text-sm font-semibold">{radio.nombre}</p>
        </div>
      </div>

      {/* Now Playing */}
      {tv?.enVivoAhora ? (
        <div className="mb-6 p-4 bg-white/10 rounded-xl border border-white/10">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
            <span className="text-xs font-semibold text-emerald-400 uppercase tracking-wider">En Vivo Ahora</span>
          </div>
          <div className="flex gap-4">
            <div className="w-24 h-16 rounded-lg overflow-hidden flex-shrink-0">
              <img src={tv.enVivoAhora.portada} alt={tv.enVivoAhora.nombre} className="w-full h-full object-cover" loading="lazy" />
            </div>
            <div>
              <h4 className="text-sm font-bold">{tv.enVivoAhora.nombre}</h4>
              <p className="text-xs text-white/60 mt-0.5">{tv.enVivoAhora.locutores[0].nombre}</p>
              <p className="text-xs text-white/40 mt-1">{tv.enVivoAhora.horarios[0].inicio} - {tv.enVivoAhora.horarios[0].fin}</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="mb-6 p-4 bg-white/5 rounded-xl border border-white/5 text-center">
          <p className="text-sm text-white/50">Sin transmisión en vivo ahora</p>
        </div>
      )}

      {/* Next */}
      {tv?.proximoPrograma && (
        <div className="mb-6 p-4 bg-white/5 rounded-xl border border-white/5">
          <div className="flex items-center gap-2 mb-3">
            <i className="ri-skip-forward-line text-white/60 text-sm"></i>
            <span className="text-xs font-semibold text-white/60 uppercase tracking-wider">Próximo Programa</span>
          </div>
          <div className="flex gap-3">
            <div className="w-16 h-12 rounded-lg overflow-hidden flex-shrink-0">
              <img src={tv.proximoPrograma.portada} alt={tv.proximoPrograma.nombre} className="w-full h-full object-cover" loading="lazy" />
            </div>
            <div>
              <h4 className="text-sm font-semibold">{tv.proximoPrograma.nombre}</h4>
              <p className="text-xs text-white/50">{tv.proximoPrograma.horarios[0].inicio} · {tv.proximoPrograma.locutores[0].nombre}</p>
            </div>
          </div>
        </div>
      )}

      {/* Full Schedule */}
      <div>
        <h5 className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-3">Programación Completa</h5>
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {tv?.programacionCompleta.slice(0, 8).map((prog, i) => (
            <div key={i} className="flex items-center gap-2 text-xs">
              <span className="text-white/40 font-mono w-14 flex-shrink-0">{prog.horarios[0].inicio}</span>
              <span className="text-white/80 truncate">{prog.nombre}</span>
              <span className="text-white/30 ml-auto flex-shrink-0">{prog.horarios[0].dia.slice(0, 3)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ─── TAB: REGLAS ─── */
function TabReglas() {
  return (
    <div className="space-y-4">
      {reglasProgramacionAutomatica.map((regla) => {
        const colorMap: Record<string, string> = {
          emerald: 'from-emerald-500 to-teal-500',
          sky: 'from-sky-500 to-cyan-500',
          amber: 'from-amber-500 to-orange-500',
          violet: 'from-violet-500 to-fuchsia-500',
          rose: 'from-rose-500 to-pink-500',
        };
        return (
          <div key={regla.id} className="bg-background-50 border border-background-200/70 rounded-2xl p-5 md:p-6 flex gap-4">
            <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${colorMap[regla.color] ?? 'from-foreground-500 to-foreground-600'} flex items-center justify-center flex-shrink-0`}>
              <i className={`${regla.icono} text-white text-lg`}></i>
            </div>
            <div>
              <h4 className="text-sm font-bold text-foreground-950 mb-1">{regla.titulo}</h4>
              <p className="text-xs text-foreground-500 leading-relaxed">{regla.descripcion}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}